import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, EmailHistory, EmailPreview, EmailStatusTag, EmptyState, Modal, RecipientSummary, Tabs } from "../../../ui";
import { previewRequestEmailAPI, retryRequestEmailAPI, sendRequestEmailsAPI } from "../../../../utils/api/requestEmails";
import { pick } from "./unifiedText";

// Preview -> confirm -> send for a request e-mail.
//   * opening the dialog only asks the server for a draft: nothing is sent and
//     nothing is changed;
//   * the exact messages (To, Cc, subject, body, attachments) of every
//     application are shown; sending needs an explicit confirmation;
//   * the send quotes the reviewed draft's hash and a stable idempotency key
//     (one per dialog), so a double click or a retry cannot send twice; if the
//     server's draft changed meanwhile, the new draft is shown for a new review;
//   * a partial purpose (cashier workflow) lets the user choose which applications get
//     the message now: every unsent application with a recipient starts selected, sent /
//     in-progress / recipient-less ones cannot be selected, and only the selected names
//     are sent - the server re-validates them against the request's own targets and
//     leaves the others pending for a later send;
//   * the result is shown per application; failed ones can be retried (the same
//     stored message is re-sent - the server refuses anything that already went out).

const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);
// applications that can be chosen now: a message exists (so a recipient) and none went out yet
const eligibleOf = (draft) => (draft?.partial ? (draft.messages || []).filter((message) => !message.existing).map((message) => message.application) : []);
const errorText = (error, t) => t.email.errors[error?.code] || t.branch?.errors?.[error?.code] || error?.message || t.action.failed;

export default function SendEmailDialog({ request, purpose, t, lang, onClose, onChanged }) {
  const token = localStorage.getItem("auth-token");
  const labels = t.email;
  const { source, id } = request.identity;
  const [state, setState] = useState({ phase: "loading", draft: null, error: null });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("");
  const [results, setResults] = useState(null);
  const [missingAfter, setMissingAfter] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const [selected, setSelected] = useState([]);
  const key = useRef(newKey());

  const load = useCallback(() => {
    const controller = new AbortController();
    setState({ phase: "loading", draft: null, error: null });
    previewRequestEmailAPI(token, source, id, { purpose: purpose.code, lang }, { signal: controller.signal })
      .then((draft) => {
        setState({ phase: "ready", draft, error: null });
        setSelected(eligibleOf(draft));
        setTab(eligibleOf(draft)[0] || draft.messages[0]?.application || "");
      })
      .catch((failure) => {
        if (failure?.name !== "AbortError") setState({ phase: "error", draft: null, error: failure });
      });
    return () => controller.abort();
  }, [token, source, id, purpose.code, lang]);
  useEffect(() => load(), [load]);

  const draft = state.draft;

  const send = async () => {
    if (busy || !confirmed || !draft?.canSend || (draft.partial && !selected.length)) return;
    setBusy(true);
    setError("");
    try {
      const response = await sendRequestEmailsAPI(token, source, id, { purpose: purpose.code, lang, draftHash: draft.draftHash, idempotencyKey: key.current, ...(draft.partial ? { applications: selected } : {}) });
      setResults(response.emails);
      setMissingAfter(response.missing || []);
      await onChanged?.();
    } catch (failure) {
      if (failure?.code === "DRAFT_CHANGED" && failure.details?.draft) {
        // show the new version for a new review; the old confirmation no longer applies
        setState({ phase: "ready", draft: failure.details.draft, error: null });
        setSelected((now) => eligibleOf(failure.details.draft).filter((name) => now.includes(name)));
        setConfirmed(false);
        setNotice(labels.draftChanged);
        key.current = newKey();
      } else {
        setError(errorText(failure, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const retry = async (email) => {
    if (retrying) return;
    setRetrying(email.id);
    setError("");
    try {
      const response = await retryRequestEmailAPI(token, email.id, {});
      setResults((current) => current.map((item) => (item.id === email.id ? { ...response.email, label: item.label } : item)));
      await onChanged?.();
    } catch (failure) {
      setError(errorText(failure, t));
    } finally {
      setRetrying(null);
    }
  };

  const allSent = results && results.every((email) => email.status === "sent") && missingAfter.length === 0;
  const labelByApp = new Map((draft?.applications || []).map((app) => [app.application, app.label]));
  const shownResults = results ? results.map((email) => ({ ...email, label: email.label || labelByApp.get(email.application) })) : null;
  const partial = Boolean(draft?.partial);
  const eligible = eligibleOf(draft);
  const sendable = partial ? (draft?.messages || []).filter((message) => selected.includes(message.application)) : (draft?.messages || []).filter((message) => !message.existing);
  const toggle = (name) => {
    setSelected((now) => (now.includes(name) ? now.filter((item) => item !== name) : [...now, name]));
    setConfirmed(false);
  };
  const setAll = (all) => {
    setSelected(all ? eligible : []);
    setConfirmed(false);
  };
  const missingByApp = new Map((draft?.missing || []).map((item) => [item.application, item]));
  const nameOf = (message) => pick(message.label, lang) || message.application;
  // a partial purpose (cashier workflow) sends what it can; the rest is reported, not blocking
  const summaryLabels = draft?.partial ? { ...labels, missingTitle: labels.missingPartial } : labels;
  const previewed = partial ? sendable : draft?.messages || [];
  const tabs = previewed.map((message) => ({ key: message.application, label: pick(message.label, lang) }));
  const current = previewed.find((message) => message.application === tab) || previewed[0];

  return (
    <Modal
      title={labels.dialogTitle(pick(purpose.label, lang))}
      description={results ? undefined : labels.reviewNote}
      size="lg"
      busy={busy}
      onClose={onClose}
      closeLabel={labels.close}
      className="lud-ur-modal lud-ur-email-dialog"
      footer={
        results ? (
          <Button variant="neutral" appearance="soft" onClick={onClose}>{labels.close}</Button>
        ) : (
          <>
            <Button variant="primary" appearance="filled" disabled={busy || !confirmed || !draft?.canSend || (partial && !selected.length)} loading={busy} onClick={send}>
              {busy ? labels.sending : partial ? labels.sendSelected(selected.length) : labels.sendNow}
            </Button>
            <Button variant="neutral" appearance="soft" disabled={busy} onClick={onClose}>{t.action.cancelDialog}</Button>
          </>
        )
      }
    >
      {state.phase === "loading" && <EmptyState variant="loading" compact>{labels.previewing}</EmptyState>}
      {state.phase === "error" && (
        <EmptyState variant="error" compact title={labels.previewError} action={<Button size="sm" onClick={load}>{t.retry}</Button>}>
          {errorText(state.error, t)}
        </EmptyState>
      )}

      {state.phase === "ready" && !results && (
        <div className="lud-ur-dialog-body">
          {notice && <p className="lud-ur-error" role="status">{notice}</p>}
          {partial ? (
            <section className="lud-ur-select" aria-label={labels.selectTitle}>
              <div className="lud-ur-select-head">
                <h4>{labels.selectTitle}</h4>
                <span className="lud-ur-select-tools">
                  <Button size="sm" variant="neutral" appearance="soft" disabled={busy || selected.length === eligible.length || !eligible.length} onClick={() => setAll(true)}>{labels.selectAll}</Button>
                  <Button size="sm" variant="neutral" appearance="soft" disabled={busy || !selected.length} onClick={() => setAll(false)}>{labels.deselectAll}</Button>
                </span>
              </div>
              <ul className="lud-ur-select-list">
                {(draft.applications || []).map((app) => {
                  const message = draft.messages.find((item) => item.application === app.application);
                  const lack = missingByApp.get(app.application);
                  const status = message?.existing?.status || message?.lastStatus || "none";
                  const selectable = Boolean(message) && !message.existing;
                  return (
                    <li key={app.application} className={selectable ? "" : "is-locked"}>
                      <Checkbox
                        checked={selectable && selected.includes(app.application)}
                        disabled={!selectable || busy}
                        onChange={() => toggle(app.application)}
                        aria-label={pick(app.label, lang)}
                        label={<strong>{pick(app.label, lang)}</strong>}
                      />
                      <span className="ui-email-addresses" dir="ltr">{message ? message.to.join(", ") : ""}</span>
                      {lack && <span className="lud-ur-select-missing">{labels.missing[lack.code] || lack.message}</span>}
                      {message?.existing && <span className="lud-ur-muted">{labels.alreadySent}</span>}
                      <EmailStatusTag status={status} labels={labels} />
                    </li>
                  );
                })}
              </ul>
              {draft.warnings?.map((warning) => <p key={warning.code} className="ui-email-warning">{labels.warnings?.[warning.code] || warning.message}</p>)}
              {draft.missing.length > 0 && <p className="lud-ur-muted">{labels.partialNote}</p>}
              <p className="lud-ur-muted" role="status">{labels.selectedCount(selected.length, eligible.length)}</p>
              {selected.length === 0 && eligible.length > 0 && <p className="lud-ur-error" role="alert">{labels.selectOne}</p>}
            </section>
          ) : (
            <>
              <RecipientSummary applications={draft.applications} messages={draft.messages} missing={draft.missing} warnings={draft.warnings} labels={summaryLabels} lang={lang} />
            </>
          )}
          {previewed.length > 0 && (
            <>
              <Tabs tabs={tabs} value={current?.application} onChange={setTab} label={labels.recipients} idPrefix="lud-ur-mail" rtl={lang === "ar"} />
              <div role="tabpanel" id={`lud-ur-mail-panel-${current?.application}`} aria-labelledby={`lud-ur-mail-tab-${current?.application}`}>
                {current?.existing && <p className="lud-ur-muted" role="note">{labels.alreadySent}</p>}
                {current && <EmailPreview message={current} labels={labels} lang={lang} />}
              </div>
            </>
          )}
          {draft.canSend && (!partial || selected.length > 0) && (
            <Checkbox
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              label={partial ? labels.confirmSelected(sendable.map(nameOf)) : labels.confirm(sendable.length)}
            />
          )}
          {error && <p className="lud-ur-error" role="alert">{error}</p>}
        </div>
      )}

      {results && (
        <div className="lud-ur-dialog-body">
          <h3>{labels.sentTitle}</h3>
          <p role="status">{allSent ? labels.allSent : labels.someNotSent}</p>
          {missingAfter.length > 0 && (
            <ul className="lud-ur-plain">{missingAfter.map((item) => <li key={item.application || item.code}>{pick(item.label, lang) || item.application}: {labels.missing[item.code] || item.message}</li>)}</ul>
          )}
          <EmailHistory emails={shownResults} labels={labels} lang={lang} onRetry={retry} retryable={(email) => email.status === "failed"} busyId={retrying} />
          {error && <p className="lud-ur-error" role="alert">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
